import React from 'react';
import { Box, ContentPanel, DataTable } from '@hopskipdrive/react-components';

import type { Recording } from '../../../api/services/Recordings';
import { useTimezone } from '../../../hooks/useTimezone';
import { Button } from '../../../shared/components/Button/Button';
import { TablePaginationContainer } from '../../../shared/components/TablePaginationContainer';
import { Text } from '../../../shared/components/Text';
import { RecordingsQueryParams, useRecordingsQuery } from '../../hooks/useRecordingsQuery';

import { getRecordingsColumns } from './columns';

const TABLE_HEIGHT_OFFSET = 100_000;

interface EmptyStateProps {
  onReset: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ onReset }) => (
  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap="20px" py="40px">
    <Text fontWeight={700} style={{ fontSize: '16px', color: '#242526' }}>
      No recordings found
    </Text>
    <Text textColor="secondaryText" textAlign="center" style={{ maxWidth: '400px' }}>
      No recordings found. Try adjusting the date or rider filters to see recordings.
    </Text>
    <Button color="tertiary" size="large" onClick={onReset}>
      Reset Filters
    </Button>
  </Box>
);

interface RecordingsTableProps {
  queryParams: RecordingsQueryParams;
  onPageChange: (params: { page: number; perPage: number }) => void;
  onResetFilters: () => void;
  onDownload: (recording: Recording) => void;
}

export const RecordingsTable: React.FC<RecordingsTableProps> = ({
  queryParams,
  onPageChange,
  onResetFilters,
  onDownload,
}) => {
  const { recordings, isLoading, isSuccess, totalCount } = useRecordingsQuery(queryParams);
  const { formatInTimezone } = useTimezone();
  const columns = getRecordingsColumns(onDownload, formatInTimezone);

  const filterKey = `${queryParams.riderName ?? ''}-${queryParams.startDate ?? ''}-${queryParams.endDate ?? ''}`;

  // isSuccess is true after the first successful fetch — prevents empty state flashing on initial load.
  const showEmptyState = isSuccess && !isLoading && recordings.length === 0;

  return (
    <ContentPanel sx={{ p: '10px', borderRadius: '4px', overflowX: 'auto', overflowY: 'hidden' }}>
      <TablePaginationContainer
        totalCount={totalCount}
        handlePageChange={onPageChange}
        filterKey={filterKey}
        isFetching={isLoading}
        heightOffset={TABLE_HEIGHT_OFFSET}
      >
        <DataTable
          stickyHeader
          isLoading={isLoading}
          columns={columns}
          rows={recordings}
          handleRowClick={() => undefined}
          noRecordsMessage={showEmptyState ? <EmptyState onReset={onResetFilters} /> : null}
        />
      </TablePaginationContainer>
      {!isLoading && recordings.length > 0 && (
        <Box display="flex" justifyContent="center" py="20px">
          <Text textColor="secondaryText">Recordings available for 30 days after the ride</Text>
        </Box>
      )}
    </ContentPanel>
  );
};
